<template>
  <div
    id="asset-modbus-param-set"
    class="assets-table-container"
  >
    <div class="modbus-param-set-card">
      <div class="modbus-param-set-card__header">
        <i class="bi bi-sliders modbus-param-set-card__icon" />
        <span class="modbus-param-set-card__title">{{ $t('components.assetModbusParamSet.title') }}</span>
      </div>

      <div class="modbus-param-form">
        <!-- Column 1: Asset selector -->
        <div class="modbus-param-form__group">
          <label
            class="modbus-param-form__label"
            for="modbus-asset-select"
          >
            {{ $t('components.assetModbusParamSet.assetSerial') }}
          </label>
          <select
            id="modbus-asset-select"
            v-model="selectedSerialNumber"
            class="form-select modbus-param-form__select"
            :disabled="loadingAssets"
            @change="onAssetChange"
          >
            <option
              :value="null"
              disabled
            >
              {{ loadingAssets ? $t('components.assetModbusParamSet.loading') : $t('components.assetModbusParamSet.selectAsset') }}
            </option>
            <option
              v-for="asset in assets"
              :key="asset.serial_number"
              :value="asset.serial_number"
            >
              {{ asset.name ? `${asset.serial_number} (${asset.name})` : asset.serial_number }}
            </option>
          </select>
          <transition name="modbus-slide-down">
            <div
              v-if="selectedAsset"
              class="modbus-param-form__meta"
            >
              <span class="modbus-meta-chip">
                <i class="bi bi-tag-fill" />
                {{ selectedAsset.name }}
              </span>
              <span class="modbus-meta-chip modbus-meta-chip--muted">
                <i class="bi bi-cpu" />
                {{ selectedAsset.imei }}
              </span>
            </div>
          </transition>
        </div>

        <!-- Column 2: Unified param + DKN action selector -->
        <div class="modbus-param-form__group">
          <label
            class="modbus-param-form__label"
            for="modbus-param-select"
          >
            {{ $t('components.assetModbusParamSet.parameter') }}
          </label>
          <select
            id="modbus-param-select"
            v-model="selectedParamName"
            class="form-select modbus-param-form__select"
            :disabled="loadingParams || loadingDkn || !selectedSerialNumber"
            @change="onParamChange"
          >
            <option
              :value="null"
              disabled
            >
              {{ (loadingParams || loadingDkn) ? $t('components.assetModbusParamSet.loading') : $t('components.assetModbusParamSet.selectParameter') }}
            </option>

            <!-- DKN canonical action groups (shown first for DKN devices) -->
            <optgroup
              v-for="group in groupedDknActions"
              :key="'dkn_' + group.key"
              :label="'★ ' + group.label"
            >
              <option
                v-for="a in group.actions"
                :key="a.canonical_key"
                :value="DKN_PREFIX + a.canonical_key"
              >
                {{ a.action_type === 'readonly' ? `○ ${a.label || a.canonical_key}` : `● ${a.label || a.canonical_key}` }}
              </option>
            </optgroup>

            <!-- Regular IO params -->
            <optgroup
              v-for="group in groupedParams"
              :key="group.label"
              :label="group.label"
            >
              <option
                v-for="param in group.params"
                :key="param.id"
                :value="param.param_name"
              >
                {{ param.description || param.param_name }}
              </option>
            </optgroup>
          </select>

          <transition name="modbus-slide-down">
            <div
              v-if="selectedDknAction"
              class="modbus-param-form__hint"
            >
              <i class="bi bi-code-slash" />
              <code>{{ selectedDknAction.canonical_key }}</code>
              <span
                v-if="selectedDknAction.action_type === 'readonly'"
                class="dkn-readonly-badge"
              >
                <i class="bi bi-eye" />
                {{ $t('components.assetDknControlPanel.readOnlyBadge') }}
              </span>
            </div>
            <div
              v-else-if="selectedParam"
              class="modbus-param-form__hint"
            >
              <i class="bi bi-code-slash" />
              <code>{{ selectedParam.param_name }}</code>
            </div>
          </transition>
        </div>

        <!-- Column 3: Adaptive value / action input -->
        <div class="modbus-param-form__group">
          <label class="modbus-param-form__label">
            {{ $t('components.assetModbusParamSet.value') }}
          </label>

          <!-- DKN toggle: ON / OFF buttons -->
          <template v-if="selectedDknAction?.action_type === 'toggle'">
            <div class="modbus-param-form__input-row">
              <button
                class="modbus-param-submit-btn dkn-btn--on"
                :disabled="sending"
                @click="submitDkn(1)"
              >
                <span
                  v-if="sending && dknPendingValue === 1"
                  class="spinner-border spinner-border-sm modbus-param-submit-btn__spinner"
                  role="status"
                />
                <i
                  v-else
                  class="bi bi-power modbus-param-submit-btn__icon"
                />
                {{ $t('components.assetDknControlPanel.actions.on') }}
              </button>
              <button
                class="modbus-param-submit-btn dkn-btn--off"
                :disabled="sending"
                @click="submitDkn(0)"
              >
                <span
                  v-if="sending && dknPendingValue === 0"
                  class="spinner-border spinner-border-sm modbus-param-submit-btn__spinner"
                  role="status"
                />
                <i
                  v-else
                  class="bi bi-stop-circle modbus-param-submit-btn__icon"
                />
                {{ $t('components.assetDknControlPanel.actions.off') }}
              </button>
            </div>
          </template>

          <!-- DKN setpoint: number input + Set button -->
          <template v-else-if="selectedDknAction?.action_type === 'setpoint'">
            <div class="modbus-param-form__input-row">
              <input
                v-model.number="dknNumericValue"
                type="number"
                class="form-control modbus-param-form__input"
                :min="selectedDknAction.value_min ?? undefined"
                :max="selectedDknAction.value_max ?? undefined"
                :step="selectedDknAction.value_step ?? 1"
                :placeholder="selectedDknAction.unit ? `${$t('components.assetDknControlPanel.valuePlaceholder')} (${selectedDknAction.unit})` : $t('components.assetDknControlPanel.valuePlaceholder')"
                :disabled="sending"
                @keyup.enter="submitDkn(dknNumericValue)"
              >
              <button
                class="modbus-param-submit-btn"
                :disabled="sending || dknNumericValue === null"
                @click="submitDkn(dknNumericValue)"
              >
                <span
                  v-if="sending"
                  class="spinner-border spinner-border-sm modbus-param-submit-btn__spinner"
                  role="status"
                />
                <i
                  v-else
                  class="bi bi-send-fill modbus-param-submit-btn__icon"
                />
                {{ sending ? $t('components.assetDknControlPanel.sending') : $t('components.assetDknControlPanel.actions.set') }}
              </button>
            </div>
          </template>

          <!-- DKN command: Execute button -->
          <template v-else-if="selectedDknAction?.action_type === 'command'">
            <div class="modbus-param-form__input-row">
              <button
                class="modbus-param-submit-btn"
                :disabled="sending"
                @click="submitDkn(1)"
              >
                <span
                  v-if="sending"
                  class="spinner-border spinner-border-sm modbus-param-submit-btn__spinner"
                  role="status"
                />
                <i
                  v-else
                  class="bi bi-play-circle modbus-param-submit-btn__icon"
                />
                {{ sending ? $t('components.assetDknControlPanel.sending') : $t('components.assetDknControlPanel.actions.run') }}
              </button>
            </div>
          </template>

          <!-- DKN readonly hint -->
          <template v-else-if="selectedDknAction?.action_type === 'readonly'">
            <div class="modbus-param-form__hint">
              <i class="bi bi-eye" />
              {{ $t('components.assetDknControlPanel.readOnly') }}
            </div>
            <div class="modbus-param-form__hint">
              <i class="bi bi-activity" />
              {{ $t('components.assetDknControlPanel.latestValue') }}:
              <code>{{ selectedDknAction.latest_value?.param_name || selectedDknAction.read_param }}</code>
              <span>
                {{ selectedDknAction.latest_value?.value ?? $t('components.assetDknControlPanel.noLatestValue') }}
              </span>
              <span v-if="selectedDknAction.latest_value?.created_at">
                {{ $t('components.assetDknControlPanel.latestRecordAt') }}:
                {{ formatLatestRecordDate(selectedDknAction.latest_value.created_at) }}
              </span>
            </div>
          </template>

          <!-- DKN UART (Daikin Mini Zeas, model 31): wyłącznie odczyt z bazy (cykliczny dump z teltoniki) -->
          <template v-else-if="isDknUart && selectedParam">
            <div class="modbus-param-form__hint">
              <i class="bi bi-eye" />
              {{ $t('components.assetDknControlPanel.readOnly') }}
            </div>
            <div
              v-if="selectedDknUartGroup"
              class="modbus-param-form__hint"
            >
              <i class="bi bi-collection" />
              <code>{{ selectedDknUartGroup.code }}</code>
              <span>{{ selectedDknUartGroup.label }}</span>
            </div>
            <div
              v-if="loadingDknUartLatest"
              class="modbus-param-form__hint"
            >
              <span
                class="spinner-border spinner-border-sm"
                role="status"
              />
              {{ $t('components.assetModbusParamSet.loading') }}
            </div>
            <div
              v-else
              class="modbus-readout"
            >
              <div class="modbus-readout__row">
                <span
                  v-if="dknUartLatestValue?.value !== undefined && dknUartLatestValue?.value !== null"
                  class="modbus-readout__value"
                >{{ dknUartLatestValue.value }}</span>
                <span
                  v-else
                  class="modbus-readout__value modbus-readout__value--empty"
                >
                  {{ $t('components.assetDknControlPanel.noLatestValue') }}
                </span>
                <span
                  v-if="dknUartLatestValue?.created_at"
                  class="modbus-readout__timestamp"
                >
                  <i class="bi bi-clock" />
                  {{ formatLatestRecordDate(dknUartLatestValue.created_at) }}
                </span>
              </div>
              <div class="modbus-readout__param">
                <i class="bi bi-tag" />
                <code>{{ dknUartLatestValue?.param_name || selectedParam.param_name }}</code>
              </div>
            </div>
          </template>

          <!-- Regular param: text input + Set button -->
          <template v-else>
            <div class="modbus-param-form__input-row">
              <input
                id="modbus-param-value"
                v-model="paramValue"
                type="text"
                class="form-control modbus-param-form__input"
                :placeholder="$t('components.assetModbusParamSet.valuePlaceholder')"
                :disabled="!selectedParamName || sending"
                @keyup.enter="submitParam"
              >
              <button
                class="modbus-param-submit-btn"
                :disabled="!canSubmit || sending"
                @click="submitParam"
              >
                <span
                  v-if="sending"
                  class="spinner-border spinner-border-sm modbus-param-submit-btn__spinner"
                  role="status"
                />
                <i
                  v-else
                  class="bi bi-send-fill modbus-param-submit-btn__icon"
                />
                {{ sending ? $t('components.assetModbusParamSet.sending') : $t('components.assetModbusParamSet.set') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <transition name="modbus-status-fade">
      <div
        v-if="verifyStage"
        :class="['modbus-verify-card', `modbus-verify-card--${statusVariant}`]"
      >
        <div class="modbus-verify-card__inner">
          <span
            v-if="isStatusLoading"
            class="spinner-border spinner-border-sm modbus-verify-card__spinner"
            role="status"
          />
          <i
            v-else
            :class="['bi', statusIcon, 'modbus-verify-card__icon']"
          />
          <div class="modbus-verify-card__body">
            <p class="modbus-verify-card__message">
              {{ statusMessage }}
            </p>
            <code
              v-if="verifyReadResult && verifyStage === 'success'"
              class="modbus-verify-card__raw"
            >{{ verifyReadResult }}</code>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  getModbusAssetsList,
  getCanParamsIo,
  getModbusParamSetStatus,
  getModbusParamReadFrame,
  getModbusParamLatestValue,
  getDeviceSerialCommandResult,
  setModbusAssetParam,
  decodeModbusReadResponse,
  clearDeviceSerialCommandResult,
  setDeviceSerialCommand,
  getDknControlPanel,
  postDknControlAction,
} from '@/crud/modbus'
import { getHumanDateTimeFromDateTime } from '@/utils/tools'

type VerifyStage = null | 'write_pending' | 'write_done' | 'read_pending' | 'success' | 'write_error' | 'timeout'

interface ModbusAsset {
  serial_number: string
  name: string
  imei: string
  device_model_id: number | string | null
}

interface ModbusParam {
  id: number
  param_name: string
  description: string | null
  writable?: boolean
}

interface ParamGroup {
  label: string
  params: ModbusParam[]
}

interface DknAction {
  canonical_key: string
  label: string | null
  action_group: string
  action_type: 'toggle' | 'setpoint' | 'command' | 'readonly'
  read_param: string | null
  write_param_on: string | null
  write_param_off: string | null
  value_min: number | null
  value_max: number | null
  value_step: number | null
  unit: string | null
  latest_value: {
    param_name: string
    value: string
    asset_timestamp: number | null
    created_at: string | null
  } | null
}

interface DknGroup {
  key: string
  label: string
  actions: DknAction[]
}

const DKN_MODEL_IDS = new Set([23, 25, 30])
const DKN_UART_MODEL_IDS = new Set([31])
const DKN_PREFIX = '__dkn__'

const DKN_UART_GROUP_ORDER = ['D00', 'D10', 'D20', 'D21', 'D30', 'D50', 'D70']

function extractDknUartGroupCode(paramName: string): string | null {
  const match = paramName.match(/^DKN_UART_(D\d{2})_/)
  return match ? match[1] : null
}

export default defineComponent({
  name: 'AssetModbusParamSet',
  setup() {
    const { t } = useI18n()
    return { t }
  },
  data() {
    return {
      assets:            [] as ModbusAsset[],
      allParams:         [] as ModbusParam[],
      dknActions:        [] as DknAction[],
      selectedSerialNumber: null as string | null,
      selectedParamName: null as string | null,
      paramValue:        '',
      dknNumericValue:   null as number | null,
      dknPendingValue:   null as number | null,
      loadingAssets:     false,
      loadingParams:     false,
      loadingDkn:        false,
      sending:           false,
      readBackStarted:   false,
      verifyStage:       null as VerifyStage,
      verifyReadResult:  null as string | null,
      verifyDecodedValue:null as string | null,
      verifyParamName:   null as string | null,
      verifySerialNumber:null as string | null,
      verifyReadParam:   null as string | null,
      pollTimer:         null as ReturnType<typeof window.setInterval> | null,
      pollAttempts:      0,
      dknUartLatestValue: null as { param_name: string; value: string; created_at: string | null } | null,
      loadingDknUartLatest: false,
      DKN_PREFIX,
    }
  },
  computed: {
    selectedAsset(): ModbusAsset | null {
      return this.assets.find(a => a.serial_number === this.selectedSerialNumber) ?? null
    },
    isDkn(): boolean {
      const modelId = this.selectedAsset?.device_model_id
      return modelId !== null && modelId !== undefined && DKN_MODEL_IDS.has(Number(modelId))
    },
    isDknUart(): boolean {
      const modelId = this.selectedAsset?.device_model_id
      return modelId !== null && modelId !== undefined && DKN_UART_MODEL_IDS.has(Number(modelId))
    },
    selectedDknUartGroup(): { code: string; label: string } | null {
      if (!this.isDknUart || !this.selectedParamName) return null
      const code = extractDknUartGroupCode(this.selectedParamName)
      if (!code) return null
      return { code, label: this.dknUartGroupLabel(code) }
    },
    selectedDknAction(): DknAction | null {
      if (!this.selectedParamName?.startsWith(DKN_PREFIX)) return null
      const key = this.selectedParamName.slice(DKN_PREFIX.length)
      return this.dknActions.find(a => a.canonical_key === key) ?? null
    },
    groupedDknActions(): DknGroup[] {
      if (!this.isDkn) return []
      const map: Record<string, DknAction[]> = {}
      for (const a of this.dknActions) {
        if (!map[a.action_group]) map[a.action_group] = []
        map[a.action_group].push(a)
      }
      return Object.keys(map).map(key => ({
        key,
        label: this.t(`components.assetDknControlPanel.groups.${key}`, key),
        actions: map[key],
      }))
    },
    modbusParams(): ModbusParam[] {
      const modelId = this.selectedAsset?.device_model_id ?? null
      const FW_1_7_ONLY = ['ZAN_FMU', 'ZAN_DPI', 'ZAN_DTE']
      const filtered = this.allParams.filter(p => {
        const name = String(p.param_name ?? '')
        if (modelId === 22 || modelId === '22') return name.startsWith('WAGO_')
        if (modelId === 23 || modelId === '23') return name.startsWith('DKN_LMS_')
        if (modelId === 25 || modelId === '25' || modelId === 30 || modelId === '30') return name.startsWith('DKN_MPS_')
        if (modelId === 27 || modelId === '27') return name.startsWith('ZAN_') && !FW_1_7_ONLY.includes(name)
        if (modelId === 28 || modelId === '28') return name.startsWith('ZAN_')
        if (modelId === 31 || modelId === '31') return name.startsWith('DKN_UART_')
        return name.startsWith('WAGO_') || name.startsWith('DKN_LMS_') || name.startsWith('DKN_MPS_') || name.startsWith('ZAN_')
      })
      // DKN UART (Mini Zeas, model 31): wszystkie params są tylko do odczytu — wartości pochodzą
      // z cyklicznego odpytywania przez teltonika-server i są dostępne w asset_params_data.
      return this.isDknUart ? filtered.map(p => ({ ...p, writable: false })) : filtered
    },
    groupedParams(): ParamGroup[] {
      if (this.isDknUart) {
        const groups: Record<string, ModbusParam[]> = {}
        for (const param of this.modbusParams) {
          const code = extractDknUartGroupCode(String(param.param_name ?? '')) ?? 'other'
          if (!groups[code]) groups[code] = []
          groups[code].push(param)
        }
        return Object.keys(groups)
          .sort((a, b) => {
            const ai = DKN_UART_GROUP_ORDER.indexOf(a); const bi = DKN_UART_GROUP_ORDER.indexOf(b)
            if (ai === -1 && bi === -1) return a.localeCompare(b)
            if (ai === -1) return 1
            if (bi === -1) return -1
            return ai - bi
          })
          .map(code => ({ label: this.dknUartGroupLabel(code), params: groups[code] }))
      }

      const groups: Record<string, ModbusParam[]> = {}
      for (const param of this.modbusParams) {
        const name = String(param.param_name ?? '')
        const label = name.startsWith('WAGO_') ? 'WAGO' : name.startsWith('ZAN_') ? 'Zanotti' : name.startsWith('DKN_MPS_') ? 'DKN MPS' : 'DKN LMS'
        if (!groups[label]) groups[label] = []
        groups[label].push(param)
      }
      return Object.keys(groups).sort().map(label => ({ label, params: groups[label] }))
    },
    selectedParam(): ModbusParam | null {
      if (this.selectedParamName?.startsWith(DKN_PREFIX)) return null
      return this.modbusParams.find(p => p.param_name === this.selectedParamName) ?? null
    },
    canSubmit(): boolean {
      if (!this.selectedSerialNumber || !this.selectedParamName) return false
      if (this.selectedDknAction) {
        if (this.selectedDknAction.action_type === 'readonly') return false
        if (this.selectedDknAction.action_type === 'setpoint') return this.dknNumericValue !== null
        return true
      }
      return this.paramValue.trim() !== '' && this.selectedParam?.writable !== false
    },
    isStatusLoading(): boolean {
      return this.verifyStage === 'write_pending' || this.verifyStage === 'read_pending'
    },
    statusVariant(): string {
      switch (this.verifyStage) {
        case 'success':     return 'success'
        case 'write_error': return 'danger'
        case 'timeout':     return 'warning'
        default:            return 'info'
      }
    },
    statusIcon(): string {
      switch (this.verifyStage) {
        case 'write_done':  return 'bi-arrow-repeat'
        case 'success':     return 'bi-check-circle-fill'
        case 'write_error': return 'bi-x-circle-fill'
        case 'timeout':     return 'bi-clock-fill'
        default:            return ''
      }
    },
    statusMessage(): string {
      switch (this.verifyStage) {
        case 'write_pending': return this.t('components.assetModbusParamSet.status.writePending')
        case 'write_done':    return this.t('components.assetModbusParamSet.status.writeDone')
        case 'read_pending':  return this.t('components.assetModbusParamSet.status.readPending')
        case 'success':
          return this.t('components.assetModbusParamSet.status.success', {
            value: this.verifyDecodedValue ?? '',
            param: this.verifyParamName ?? '',
            serial: this.verifySerialNumber ?? ''
          })
        case 'write_error': return this.t('components.assetModbusParamSet.status.writeError')
        case 'timeout':     return this.t('components.assetModbusParamSet.status.timeout')
        default:            return ''
      }
    }
  },
  async mounted() {
    await Promise.all([this.loadAssets(), this.loadParams()])
  },
  beforeUnmount() {
    this.stopPoll()
  },
  methods: {
    onAssetChange() {
      this.selectedParamName = null
      this.paramValue = ''
      this.dknActions = []
      this.dknNumericValue = null
      this.dknPendingValue = null
      this.verifyStage = null
      this.verifyReadResult = null
      this.dknUartLatestValue = null
      this.stopPoll()
      // Model 31 (Mini Zeas / DKN UART) nie ma device_control_action — pomijamy panel DKN.
      if (this.isDkn && !this.isDknUart && this.selectedSerialNumber) {
        this.loadDknActions()
      }
    },
    onParamChange() {
      this.paramValue = ''
      this.dknNumericValue = null
      this.dknPendingValue = null
      this.verifyStage = null
      this.verifyReadResult = null
      this.verifyDecodedValue = null
      this.dknUartLatestValue = null
      if (this.isDknUart && this.selectedParam && this.selectedSerialNumber) {
        this.loadDknUartLatestValue()
      }
    },
    dknUartGroupLabel(code: string): string {
      return this.t(`components.assetModbusParamSet.dknUart.groups.${code}`, code)
    },
    async loadDknUartLatestValue() {
      const serial = this.selectedSerialNumber
      const param  = this.selectedParamName
      if (!serial || !param) return
      this.loadingDknUartLatest = true
      try {
        const res = await getModbusParamLatestValue(serial, param)
        this.dknUartLatestValue = res.data?.latest_value ?? null
      } catch {
        this.dknUartLatestValue = null
      } finally {
        this.loadingDknUartLatest = false
      }
    },
    showError(message: string) {
      (this as any).$toast?.error(message)
    },
    formatLatestRecordDate(createdAt: string): string {
      return getHumanDateTimeFromDateTime(createdAt)
    },
    async loadAssets() {
      this.loadingAssets = true
      try {
        const res = await getModbusAssetsList()
        this.assets = res.data ?? []
      } catch (e: any) {
        this.showError(e?.response?.data?.error || this.t('components.assetModbusParamSet.errors.loadAssets'))
      } finally {
        this.loadingAssets = false
      }
    },
    async loadParams() {
      this.loadingParams = true
      try {
        const res = await getCanParamsIo()
        this.allParams = res.data ?? []
      } catch (e: any) {
        this.showError(e?.response?.data?.error || this.t('components.assetModbusParamSet.errors.loadParams'))
      } finally {
        this.loadingParams = false
      }
    },
    async loadDknActions() {
      if (!this.selectedSerialNumber) return
      this.loadingDkn = true
      try {
        const res = await getDknControlPanel(this.selectedSerialNumber)
        this.dknActions = res.data?.actions ?? []
      } catch (e: any) {
        this.showError(e?.response?.data?.error || this.t('components.assetDknControlPanel.errors.loadPanel'))
      } finally {
        this.loadingDkn = false
      }
    },
    async submitParam() {
      if (!this.canSubmit || this.sending || this.selectedDknAction) return
      this.sending = true
      this.readBackStarted = false
      this.stopPoll()
      this.verifyStage = null
      this.verifyReadResult = null
      this.verifyDecodedValue = null
      this.verifyParamName = null
      this.verifySerialNumber = null

      const serial = this.selectedSerialNumber!
      const param  = this.selectedParamName!
      const imei   = this.selectedAsset?.imei

      try {
        await setModbusAssetParam(serial, param, this.paramValue.trim())
        this.paramValue = ''
        this.selectedParamName = null
        this.verifyParamName   = param
        this.verifySerialNumber = serial
        this.verifyReadParam   = param
        this.verifyStage = 'write_pending'
        this.startPollWrite(serial, imei!)
      } catch (e: any) {
        this.showError(e?.response?.data?.error || this.t('components.assetModbusParamSet.errors.setParam'))
      } finally {
        this.sending = false
      }
    },
    async submitDkn(value: unknown) {
      const action = this.selectedDknAction
      const serial = this.selectedSerialNumber
      const imei   = this.selectedAsset?.imei
      if (!action || !serial || !imei || this.sending) return

      this.sending = true
      this.dknPendingValue = typeof value === 'number' ? value : null
      this.readBackStarted = false
      this.stopPoll()
      this.verifyStage = null
      this.verifyReadResult = null
      this.verifyDecodedValue = null
      this.verifyParamName   = action.canonical_key
      this.verifySerialNumber = serial
      this.verifyReadParam   = action.read_param ?? null

      try {
        await postDknControlAction(serial, action.canonical_key, value)
        this.verifyStage = 'write_pending'
        this.startPollWrite(serial, imei)
      } catch (e: any) {
        this.showError(e?.response?.data?.error || this.t('components.assetDknControlPanel.errors.dispatch'))
      } finally {
        this.sending = false
      }
    },
    startPollWrite(serial: string, imei: string) {
      this.pollAttempts = 0
      this.pollTimer = window.setInterval(async () => {
        this.pollAttempts++
        if (this.pollAttempts > 25) {
          this.stopPoll()
          this.verifyStage = 'timeout'
          return
        }
        try {
          const res    = await getModbusParamSetStatus(serial)
          const status = res.data?.status
          if (status === 'pending') return
          this.stopPoll()
          if (status === 'done') {
            if (res.data?.success && !this.readBackStarted && this.verifyReadParam) {
              this.readBackStarted = true
              this.verifyStage = 'write_done'
              this.startReadBack(serial, this.verifyReadParam, imei)
            } else {
              this.verifyStage = res.data?.success ? 'success' : 'write_error'
            }
          } else {
            this.verifyStage = 'timeout'
          }
        } catch {
          // ignore transient polling errors
        }
      }, 2000)
    },
    async startReadBack(serial: string, param: string, imei: string) {
      try {
        const frameRes  = await getModbusParamReadFrame(serial, param)
        const frameHex  = frameRes.data?.frame_hex
        const frameImei = frameRes.data?.imei ?? imei
        await clearDeviceSerialCommandResult(frameImei)
        await setDeviceSerialCommand(frameImei, frameHex, 60)
        this.verifyStage  = 'read_pending'
        this.pollAttempts = 0
        this.pollTimer = window.setInterval(async () => {
          this.pollAttempts++
          if (this.pollAttempts > 20) {
            this.stopPoll()
            this.verifyStage = 'timeout'
            return
          }
          try {
            const result = await getDeviceSerialCommandResult(frameImei)
            if (result !== null) {
              this.stopPoll()
              clearDeviceSerialCommandResult(frameImei).catch(() => {})
              this.verifyReadResult = result
              try {
                const decoded = await decodeModbusReadResponse(param, result)
                this.verifyDecodedValue = decoded.data?.human_readable ?? result
              } catch {
                this.verifyDecodedValue = result
              }
              this.verifyStage = 'success'
            }
          } catch {
            // ignore transient polling errors
          }
        }, 2000)
      } catch {
        this.verifyStage = 'timeout'
      }
    },
    stopPoll() {
      if (this.pollTimer !== null) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }
  }
})
</script>
